import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FaDownload, FaSearch, FaBars, FaTimes } from "react-icons/fa";
import { DOWNLOAD_URL, findSiteMatches, primaryNavItems } from "../data/siteCatalog";
import "../style/TopBar.css";

const MIN_SITE_SEARCH_CHARS = 2;
const MAX_SITE_SUGGESTIONS = 8;

export default function TopBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [globalSearchValue, setGlobalSearchValue] = useState("");
  const [globalSearchFeedback, setGlobalSearchFeedback] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsStatus, setSuggestionsStatus] = useState("idle");
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuOpenRef = useRef(false);
  const suggestionTimeoutRef = useRef(null);
  const suggestionRequestRef = useRef(0);
  const searchContainerRef = useRef(null);
  const topbarRef = useRef(null);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    mobileMenuOpenRef.current = false;
    setSuggestions([]);
    setSuggestionsStatus("idle");
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateHeight = () => {
      if (!topbarRef.current) return;
      const { height } = topbarRef.current.getBoundingClientRect();
      document.documentElement.style.setProperty("--top-bar-height", `${height}px`);
    };

    updateHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeight);
      if (topbarRef.current) {
        resizeObserver.observe(topbarRef.current);
      }
    }

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      document.documentElement.style.removeProperty("--top-bar-height");
    };
  }, []);

  useEffect(() => {
    mobileMenuOpenRef.current = mobileMenuOpen;
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("topbar-menu-state", { detail: { open: mobileMenuOpen } })
      );
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleExternalToggle = () => {
      toggleMobileMenu();
    };
    window.addEventListener("topbar-toggle-menu", handleExternalToggle);
    return () => window.removeEventListener("topbar-toggle-menu", handleExternalToggle);
  }, [toggleMobileMenu]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const announceState = () => {
      window.dispatchEvent(
        new CustomEvent("topbar-menu-state", {
          detail: { open: mobileMenuOpenRef.current }
        })
      );
    };
    window.addEventListener("topbar-request-state", announceState);
    announceState();
    return () => window.removeEventListener("topbar-request-state", announceState);
  }, []);

  const getLinkClass = ({ isActive }) =>
    isActive ? "topbar__link topbar__link--active" : "topbar__link";

  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const handleGlobalSearchChange = event => {
    setGlobalSearchValue(event.target.value);
    setGlobalSearchFeedback("");
  };

  const openSearchResult = useCallback(
    result => {
      if (!result?.to) {
        return;
      }

      setGlobalSearchFeedback(`Apro "${result.title}".`);
      setSuggestions([]);
      setSuggestionsStatus("idle");
      setGlobalSearchValue("");
      setMobileMenuOpen(false);
      navigate(result.to);
    },
    [navigate]
  );

  const trimmedSearch = globalSearchValue.trim();

  useEffect(() => {
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
      suggestionTimeoutRef.current = null;
    }

    if (trimmedSearch.length === 0) {
      setSuggestions([]);
      setSuggestionsStatus("idle");
      return undefined;
    }

    if (trimmedSearch.length < MIN_SITE_SEARCH_CHARS) {
      setSuggestions([]);
      setSuggestionsStatus("too-short");
      return undefined;
    }

    setSuggestionsStatus("loading");
    const query = trimmedSearch;

    suggestionTimeoutRef.current = setTimeout(() => {
      const requestId = ++suggestionRequestRef.current;
      const results = findSiteMatches(query, MAX_SITE_SUGGESTIONS);

      if (suggestionRequestRef.current !== requestId) {
        return;
      }

      setSuggestions(results);
      setSuggestionsStatus(results.length > 0 ? "success" : "empty");
      suggestionTimeoutRef.current = null;
    }, 150);

    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
        suggestionTimeoutRef.current = null;
      }
    };
  }, [trimmedSearch]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSuggestions([]);
        setSuggestionsStatus("idle");
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("mousedown", handleClickOutside);
      }
    };
  }, []);

  const handleGlobalSearchSubmit = event => {
    event.preventDefault();

    const query = globalSearchValue.trim();
    if (!query) {
      setGlobalSearchFeedback("Scrivi cosa vuoi cercare nel sito.");
      return;
    }

    if (query.length < MIN_SITE_SEARCH_CHARS) {
      setGlobalSearchFeedback(`Digita almeno ${MIN_SITE_SEARCH_CHARS} caratteri.`);
      return;
    }

    setSuggestions([]);
    setSuggestionsStatus("idle");
    setGlobalSearchValue("");
    setMobileMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleSuggestionSelect = useCallback(
    suggestion => {
      openSearchResult(suggestion);
    },
    [openSearchResult]
  );

  const showSuggestions =
    trimmedSearch.length > 0 &&
    (suggestionsStatus === "loading" ||
      suggestionsStatus === "empty" ||
      suggestionsStatus === "too-short" ||
      suggestions.length > 0);

  return (
    <header className="topbar" ref={topbarRef}>
      <button
        type="button"
        className="topbar__menu-toggle"
        aria-expanded={mobileMenuOpen}
        aria-controls="primary-navigation"
        onClick={toggleMobileMenu}
      >
        {mobileMenuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
        <span className="topbar__sr-only">Menu</span>
      </button>

      <nav
        id="primary-navigation"
        className={`topbar__nav${mobileMenuOpen ? " topbar__nav--open" : ""}`}
      >
        {primaryNavItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={getLinkClass}
            onClick={handleNavLinkClick}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="topbar__utils">
        <a className="topbar__launcher-link" href={DOWNLOAD_URL} download>
          <FaDownload aria-hidden="true" />
          <span>Scarica launcher</span>
        </a>

        <form
          ref={searchContainerRef}
          className="topbar__search"
          onSubmit={handleGlobalSearchSubmit}
          role="search"
          aria-live="polite"
          aria-busy={suggestionsStatus === "loading"}
        >
          <div className="topbar__search-controls">
            <input
              type="search"
              className="topbar__search-input"
              placeholder="Cerca..."
              aria-label="Cerca nel sito"
              value={globalSearchValue}
              onChange={handleGlobalSearchChange}
            />
            <button
              type="submit"
              className="topbar__search-button"
              aria-label="Avvia la ricerca"
            >
              <FaSearch aria-hidden="true" />
            </button>
          </div>
          <span className="topbar__sr-only">{globalSearchFeedback}</span>
          {showSuggestions && (
            <div className="topbar__search-suggestions" role="listbox">
              {suggestionsStatus === "loading" && (
                <p className="topbar__search-hint">Cerco nelle pagine del sito...</p>
              )}
              {suggestionsStatus === "too-short" && (
                <p className="topbar__search-hint">
                  Digita almeno {MIN_SITE_SEARCH_CHARS} caratteri.
                </p>
              )}
              {suggestionsStatus === "empty" && suggestions.length === 0 && (
                <p className="topbar__search-hint">Nessuna pagina trovata. Premi invio per aprire la ricerca.</p>
              )}
              {suggestions.length > 0 && (
                <ul>
                  {suggestions.map(suggestion => (
                    <li key={suggestion.id}>
                      <button
                        type="button"
                        onClick={() => handleSuggestionSelect(suggestion)}
                        role="option"
                        aria-selected="false"
                      >
                        <span className="topbar__search-suggestion-identity">
                          <span className="topbar__search-suggestion-title">
                            {suggestion.title}
                          </span>
                          <span className="topbar__search-suggestion-meta">
                            {suggestion.description}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </div>
    </header>
  );
}
